import { useCallback, useState } from 'react'
import { getAddress, type Address, type Hex } from 'viem'
import { useAccount, usePublicClient, useSendTransaction } from 'wagmi'
import { ApiClientError, postSwap } from '../api/client'
import type { QuoteResponse, SwapResponse } from '../api/types'
import { assertSwapAddresses, assertSwapMatchesQuote } from '../lib/addresses'
import { permitJson, type SignedPermit } from '../lib/permit2'

/**
 * Quote -> calldata -> signature -> receipt.
 *
 * The one rule this whole file exists to keep: **calldata comes from a route id the server
 * issued, and from nothing else.** The request carries the id, the user's address, their
 * slippage and their deadline. It does not carry a route, a pool, a leg or an amount — if
 * it did, a caller could describe their own route through a pool they control and have the
 * server hand back a perfectly valid transaction for it. The server refuses to encode
 * anything it did not quote, and this is the client side of that contract.
 *
 * The second rule follows from it: **an expired id is not re-quoted behind the user's
 * back.** `/swap` answers `ROUTE_EXPIRED`, and this hook stops and says so. Quietly
 * fetching a fresh quote and signing it would put the user's signature on a price they
 * never saw — at the exact moment they were agreeing to a different one.
 *
 * A signed Permit2 permit, when `useApprovals` holds one, rides along in the request. It
 * authorises the router to pull the input; it describes nothing about the route, so it does
 * not bend the first rule. The response must show it at the head of the plan: an API that
 * ignored the field would return calldata that reverts for want of the allowance.
 */

export type SwapPhase =
  | { status: 'idle' }
  | { status: 'building' }
  | { status: 'simulating' }
  | { status: 'signing'; swap: SwapResponse }
  | { status: 'pending'; swap: SwapResponse; hash: Hex }
  | { status: 'success'; swap: SwapResponse; hash: Hex }
  | { status: 'expired' }
  | { status: 'error'; error: Error }

export type SwapOptions = {
  slippageBps: number
  deadlineSeconds: number
  /** From `useApprovals`: null when the Permit2 allowance is already on chain. */
  permit: SignedPermit | null
}

export type UseSwapResult = {
  phase: SwapPhase
  /** True from the moment /swap is called until the receipt lands. */
  isBusy: boolean
  execute: (quote: QuoteResponse, options: SwapOptions) => Promise<void>
  reset: () => void
}

export function useSwap(): UseSwapResult {
  const { address: recipient } = useAccount()
  const publicClient = usePublicClient()
  const { sendTransactionAsync } = useSendTransaction()
  const [phase, setPhase] = useState<SwapPhase>({ status: 'idle' })

  const execute = useCallback(
    async (quote: QuoteResponse, options: SwapOptions) => {
      if (!recipient) {
        setPhase({ status: 'error', error: new Error('connect a wallet first') })
        return
      }

      let swap: SwapResponse
      setPhase({ status: 'building' })
      try {
        swap = await postSwap({
          routeId: quote.routeId,
          recipient,
          slippageBps: options.slippageBps,
          deadlineSeconds: options.deadlineSeconds,
          ...(options.permit ? { permit: permitJson(options.permit) } : {}),
        })
      } catch (error) {
        // ROUTE_EXPIRED and ROUTE_NOT_FOUND are the same situation to a user: the plan they
        // were looking at is gone. Surfaced as its own phase so the card can refresh and
        // ask again rather than pretending a new quote is the old one.
        if (
          error instanceof ApiClientError &&
          (error.code === 'ROUTE_EXPIRED' || error.code === 'ROUTE_NOT_FOUND' || error.code === 'ROUTE_ALREADY_USED')
        ) {
          setPhase({ status: 'expired' })
          return
        }
        setPhase({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
        return
      }

      try {
        assertSwapAddresses(swap)
        assertSwapMatchesQuote(swap, {
          tokenIn: getAddress(quote.tokenIn.address),
          tokenOut: getAddress(quote.tokenOut.address),
          amountIn: quote.amountIn,
          amountOut: quote.amountOut,
        })
        const carriesPermit = swap.plan[0]?.command === 'PERMIT2_PERMIT'
        if (carriesPermit !== (options.permit !== null)) {
          throw new Error(
            options.permit
              ? 'the API did not put your Permit2 signature in the transaction — it may be older than this app. ' +
                  'Nothing was signed.'
              : 'the API added a Permit2 permit nobody signed. Nothing was signed.'
          )
        }
      } catch (error) {
        setPhase({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
        return
      }

      const transaction = {
        to: getAddress(swap.to) as Address,
        data: swap.calldata as Hex,
        value: BigInt(swap.value),
      }

      // Estimate before asking for a signature. A plan that reverts — a missing Permit2
      // allowance, a permit whose nonce was already used, a deadline already past, pool
      // state that moved past the aggregate minimum — is far better read here than as a
      // failed transaction the user paid for.
      setPhase({ status: 'simulating' })
      try {
        await publicClient?.estimateGas({ account: recipient, ...transaction })
      } catch (error) {
        setPhase({
          status: 'error',
          error: new Error(`this swap would revert on chain, so nothing was signed. ${describeRevert(error)}`, {
            cause: error,
          }),
        })
        return
      }

      setPhase({ status: 'signing', swap })
      let hash: Hex
      try {
        hash = await sendTransactionAsync(transaction)
      } catch (error) {
        setPhase({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
        return
      }

      setPhase({ status: 'pending', swap, hash })
      try {
        const receipt = await publicClient?.waitForTransactionReceipt({ hash })
        if (receipt && receipt.status === 'reverted') {
          setPhase({ status: 'error', error: new Error(`the swap reverted on chain (${hash})`) })
          return
        }
        setPhase({ status: 'success', swap, hash })
      } catch (error) {
        setPhase({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) })
      }
    },
    [recipient, publicClient, sendTransactionAsync]
  )

  const isBusy =
    phase.status === 'building' ||
    phase.status === 'simulating' ||
    phase.status === 'signing' ||
    phase.status === 'pending'

  return { phase, isBusy, execute, reset: () => setPhase({ status: 'idle' }) }
}

function describeRevert(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error)
  // viem puts the useful line first and a stack of context after it.
  return message.split('\n')[0] ?? 'the node gave no reason.'
}
